import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { Boxes, CheckCircle2, Clock3, CreditCard, PackageCheck, PackageX, Users, WalletCards } from 'lucide-react';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './ProduitsClientsDashboard.css';

const API_URL = import.meta.env.VITE_API_URL;
const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#0f766e', '#7c3aed', '#db2777', '#64748b'];

const fmtNumber = (value) => (Number(value) || 0).toLocaleString('fr-FR');
const fmtMoney = (value) => `${(Number(value) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} TND`;

const KpiCard = ({ icon: Icon, label, value, tone }) => (
  <div className={`pcd-kpi ${tone || ''}`}>
    <span className="pcd-kpi-icon"><Icon size={20} /></span>
    <span className="pcd-kpi-label">{label}</span>
    <strong>{value}</strong>
  </div>
);

const ProduitsClientsDashboard = ({ onNavigate, onLogout, currentPage }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/dashboard/produits-clients`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setDashboard(response.data.data);
      } catch (err) {
        console.error('Erreur dashboard produits clients:', err);
        setError('Impossible de charger le dashboard produits et clients.');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  const filteredCategories = useMemo(() => {
    const term = search.trim().toLowerCase();
    const categories = dashboard?.categories || [];
    if (!term) return categories;
    return categories
      .map(cat => ({
        ...cat,
        produits: (cat.produits || []).filter(produit =>
          `${produit.nomProduit} ${produit.reference} ${produit.categorie}`.toLowerCase().includes(term)
        )
      }))
      .filter(cat => cat.produits.length > 0 || cat.categorie.toLowerCase().includes(term));
  }, [dashboard, search]);

  if (loading) {
    return (
      <div className="pcd-layout">
        <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
        <main className="pcd-content"><LoadingSpinner message="Chargement du dashboard produits..." /></main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pcd-layout">
        <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
        <main className="pcd-content"><div className="pcd-error">{error}</div></main>
      </div>
    );
  }

  const indicateurs = dashboard?.indicateurs || {};
  const graphiques = dashboard?.graphiques || {};

  return (
    <div className="pcd-layout">
      <BackOfficeMenu onNavigate={onNavigate} onLogout={onLogout} currentPage={currentPage} />
      <main className="pcd-content">
        <section className="pcd-header">
          <div>
            <span>Dashboard metier</span>
            <h1>Produits & Clients</h1>
            <p>Vue rapide sur le stock disponible, les reservations, les ventes et les produits installes.</p>
          </div>
        </section>

        <section className="pcd-kpis">
          <KpiCard icon={Boxes} label="Total produits" value={fmtNumber(indicateurs.nombreTotalProduits)} tone="blue" />
          <KpiCard icon={CheckCircle2} label="Disponibles" value={fmtNumber(indicateurs.produitsDisponibles)} tone="green" />
          <KpiCard icon={Clock3} label="Reserves" value={fmtNumber(indicateurs.produitsReserves)} tone="amber" />
          <KpiCard icon={CreditCard} label="Vendus" value={fmtNumber(indicateurs.produitsVendus)} tone="red" />
          <KpiCard icon={PackageCheck} label="Installes" value={fmtNumber(indicateurs.produitsInstalles)} tone="teal" />
          <KpiCard icon={PackageX} label="Non installes" value={fmtNumber(indicateurs.produitsNonInstalles)} tone="purple" />
          <KpiCard icon={Users} label="Clients" value={fmtNumber(indicateurs.nombreTotalClients)} tone="slate" />
          <KpiCard icon={WalletCards} label="CA genere" value={fmtMoney(indicateurs.chiffreAffaires)} tone="money" />
        </section>

        <section className="pcd-finance-strip">
          <div><span>Valeur achats</span><strong>{fmtMoney(indicateurs.valeurTotaleAchats)}</strong></div>
          <div><span>Valeur ventes</span><strong>{fmtMoney(indicateurs.valeurTotaleVentes)}</strong></div>
          <div><span>Stock restant</span><strong>{fmtMoney(indicateurs.valeurStockRestant)}</strong></div>
          <div><span>Clients avec non installes</span><strong>{fmtNumber(indicateurs.clientsProduitsNonInstalles)}</strong></div>
        </section>

        <section className="pcd-charts-grid">
          <div className="pcd-panel">
            <h2>Etat global du stock</h2>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={graphiques.etatStock || []} dataKey="value" nameKey="name" innerRadius={58} outerRadius={92}>
                  {(graphiques.etatStock || []).map((entry, index) => <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(value) => fmtNumber(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="pcd-panel">
            <h2>Produits les plus vendus</h2>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={graphiques.produitsPlusVendus || []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="nomProduit" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} />
                <YAxis />
                <Tooltip formatter={(value) => fmtNumber(value)} />
                <Bar dataKey="quantiteVendue" fill="#2563eb" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="pcd-panel">
            <h2>Reservations par produit</h2>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={graphiques.produitsPlusReserves || []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="nomProduit" tick={{ fontSize: 11 }} interval={0} angle={-20} textAnchor="end" height={70} />
                <YAxis />
                <Tooltip formatter={(value) => fmtNumber(value)} />
                <Bar dataKey="quantiteReservee" fill="#f59e0b" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="pcd-panel">
            <h2>Evolution des ventes</h2>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={graphiques.evolutionVentes || []}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="mois" />
                <YAxis />
                <Tooltip formatter={(value) => fmtMoney(value)} />
                <Area type="monotone" dataKey="montant" stroke="#0f766e" fill="#ccfbf1" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="pcd-two-cols">
          <div className="pcd-panel">
            <h2>Revenus par categorie</h2>
            <div className="pcd-list">
              {(graphiques.revenusParCategorie || []).map((item, index) => (
                <div key={item.name} className="pcd-list-row">
                  <span style={{ '--dot': COLORS[index % COLORS.length] }}>{item.name}</span>
                  <strong>{fmtMoney(item.value)}</strong>
                </div>
              ))}
            </div>
          </div>
          <div className="pcd-panel">
            <h2>Top clients</h2>
            <div className="pcd-list">
              {(graphiques.topClients || []).map(client => (
                <div key={client.clientId} className="pcd-list-row">
                  <span>{client.nomClient}</span>
                  <strong>{fmtMoney(client.montantAchats)}</strong>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="pcd-table-panel">
          <div className="pcd-table-header">
            <div>
              <h2>Detail par categorie et produit</h2>
              <p>Quantites achetees, disponibles, reservees, vendues, installees et non installees.</p>
            </div>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un produit..." />
          </div>

          {filteredCategories.map(categorie => (
            <div className="pcd-category-block" key={categorie.categorie}>
              <h3>{categorie.categorie}</h3>
              <div className="pcd-table-wrap">
                <table className="pcd-table">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Achetee</th>
                      <th>Disponible</th>
                      <th>Reservee</th>
                      <th>Vendue</th>
                      <th>Installee</th>
                      <th>Non installee</th>
                      <th>Achats</th>
                      <th>Ventes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(categorie.produits || []).map(produit => (
                      <tr key={produit.produitId}>
                        <td><strong>{produit.nomProduit}</strong><span>{produit.reference}</span></td>
                        <td>{fmtNumber(produit.quantiteAchetee)}</td>
                        <td>{fmtNumber(produit.quantiteDisponible)}</td>
                        <td>{fmtNumber(produit.quantiteReservee)}</td>
                        <td>{fmtNumber(produit.quantiteVendue)}</td>
                        <td>{fmtNumber(produit.quantiteInstallee)}</td>
                        <td>{fmtNumber(produit.quantiteNonInstallee)}</td>
                        <td>{fmtMoney(produit.valeurAchats)}</td>
                        <td>{fmtMoney(produit.valeurVentes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </section>
      </main>
    </div>
  );
};

export default ProduitsClientsDashboard;
